"use client"

import { formatRelative, formatDiningDateTime, formatDiningDate, toValidDate } from "@/lib/format-datetime"

interface RelativeTimeProps {
  /** ISO string, epoch ms, or Date. Renders nothing if null/undefined/invalid. */
  value: string | number | Date | null | undefined
  /**
   * "auto" (default) — "Nm ago" / "Nh ago" / "Nd ago" / "Mon D" with the
   *   absolute form on hover. Best for activity logs and audit displays.
   * "absolute" — always show absolute date+time. Hover does not change.
   * "date-only" — calendar date without time, no relative form. For fields
   *   that semantically have no time-of-day (birthdays, contract dates).
   */
  mode?: "auto" | "absolute" | "date-only"
  /** Fallback string when value is null/empty/invalid. Defaults to em-dash. */
  fallback?: string
  /** Optional className for the wrapping <time> element */
  className?: string
}

/**
 * Renders a timestamp in a human-scannable form ("2h ago", "May 11")
 * while keeping the full absolute timestamp accessible via a `title`
 * tooltip on hover and via screen readers via the underlying <time>
 * element's `dateTime` attribute.
 *
 * Use this everywhere you'd otherwise call `new Date(...).toLocaleDateString()`
 * for an activity, log entry, "last seen at", "ordered at", etc. — anywhere
 * the gap between now and the timestamp is what the user actually cares about.
 *
 * Don't use this for:
 *   - Print outputs (no hover; use `formatDiningDateTime`/`formatDiningDate` directly)
 *   - Server components without an interactive layer (it's marked "use client")
 *   - Scheduled future dates ("Reservation at 7:00 PM tomorrow" — use absolute)
 */
export function RelativeTime({ value, mode = "auto", fallback = "—", className }: RelativeTimeProps) {
  const d = toValidDate(value)
  if (!d) return <span className={className}>{fallback}</span>

  const iso = d.toISOString()
  const absolute = formatDiningDateTime(d)

  let display: string
  if (mode === "absolute") display = absolute
  else if (mode === "date-only") display = formatDiningDate(d)
  else display = formatRelative(d)

  return (
    <time
      dateTime={iso}
      title={absolute}
      className={className}
    >
      {display}
    </time>
  )
}
